/**
 * @fileoverview Shielded Transaction Client (shared)
 *
 * Complete implementation for Zcash shielded transactions using:
 *   - WebZjs Keys-and-Send WASM for PCZT creation / signing
 *   - Wallet Scanner service for witness data and balance checking
 *   - Proper per-pool balance tracking (Transparent, Sapling, Orchard)
 *
 * Ported from WINBIT32 shielded-transaction-client.ts.
 *
 * External runtime dependencies (must be installed by the consuming project):
 *   @noble/curves, @scure/bip32, @scure/bip39
 */

import {
	getWebZjsClient,
	getPcztClass,
	getPcztSign,
	type WebZjsKeysAndSendClient,
	type IPczt,
	type IUnifiedSpendingKey,
	type ISeedFingerprint,
} from './webzjsClient';
import {
	createWalletScannerClient,
	type WalletScannerClientType,
} from './walletScannerClient';
import {
	installFetchInterceptor,
	clearNoteCacheForAddress,
} from './fetchInterceptor';
import {
	extractMerklePathSiblings,
	hasFullMerklePathWitness,
} from './merklePath';
import { toZcashOutputScript } from './zcashHelpers';
import { ecdsaSignatureToDer } from './ecdsaDer';
import {
	ZATOSHIS_PER_ZEC,
	ZIP317_MARGINAL_FEE,
	ORCHARD_MERKLE_DEPTH,
	DEFAULT_WALLET_SCANNER_URL,
	DEFAULT_ZEBRA_RPC_URL,
	DEFAULT_LIGHTWALLETD_URL,
	NOTE_CACHE_STORAGE_KEY,
	PENDING_NULLIFIER_STORAGE_KEY,
	BIRTHDAY_HINT_STORAGE_KEY,
	ZCASH_DERIVATION_PATH,
} from './constants';
import type {
	ShieldedBalance as IShieldedBalance,
	OrchardWitnessNote,
	CachedNotes,
} from './types';

// ─── Re-export balance interface under the name consumers already use ────────
export type ShieldedBalance = IShieldedBalance;

export interface ShieldedNote {
	txHash: string;
	blockHeight: number;
	value: number; // zatoshis
	pool: 'sapling' | 'orchard';
	nullifier?: string;
	commitment?: string;
}

export interface ShieldedTransactionOptions {
	mnemonic: string;
	fromAddress: string; // unified address
	transparentAddress?: string;
	toAddress: string;
	amountZec: number;
	memo?: string;
	accountIndex?: number;
	birthdayHeight?: number;
	autoDetectBirthday?: boolean;
	allowTransparentInputs?: boolean;
	skipShieldedScan?: boolean;
	commissionBps?: number;
	commissionUnifiedAddress?: string;
	onStatus?: (message: string) => void;
	/**
	 * Structured scan progress for the send/mint note search so the UI can show
	 * a determinate bar + block height even when the status text alone carries
	 * no percent. Fires roughly once per poll (~5s) during the scan.
	 */
	onProgress?: (progress: { percent?: number; currentBlock?: number; latestBlock?: number }) => void;
	/**
	 * Optional cancellation for the (potentially long) note scan. When it
	 * fires mid-scan the send rejects promptly so the caller can switch
	 * strategies (e.g. shield from transparent) instead of waiting it out.
	 */
	abortSignal?: AbortSignal;
}

// ─── Note / nullifier caching ────────────────────────────────────────────────

const noteCache = new Map<string, CachedNotes>();
const NOTE_CACHE_TTL = 30 * 60 * 1000;
const EMPTY_NOTE_CACHE_TTL = 30 * 60 * 1000;
const CACHE_CHAIN_STALENESS_BLOCKS = 20;
const PENDING_NULLIFIER_TTL_MS = 60 * 60 * 1000;
const pendingNullifiersByUfvk = new Map<string, Map<string, number>>();
let pendingNullifiersLoaded = false;

const DEFAULT_SHIELDED_FEE_ZATOSHIS = 10_000;

// ─── ZIP-317 fee calculation ─────────────────────────────────────────────────
const ZIP317_GRACE_ACTIONS = 2;
const TRANSPARENT_DUST_THRESHOLD_ZATOSHIS = 1_000;
const MIN_CONFIRMED_UTXO_CONFIRMATIONS = 1;

function calculateZip317Fee(params: {
	transparentInputs: number;
	transparentOutputs: number;
	orchardSpends: number;
	orchardOutputs: number;
	saplingSpends?: number;
	saplingOutputs?: number;
}): number {
	const {
		transparentInputs,
		transparentOutputs,
		orchardSpends,
		orchardOutputs,
		saplingSpends = 0,
		saplingOutputs = 0,
	} = params;

	const orchardActions =
		orchardSpends > 0 || orchardOutputs > 0
			? Math.max(2, Math.max(orchardSpends, orchardOutputs))
			: 0;

	const logicalActions =
		Math.max(transparentInputs, transparentOutputs) +
		orchardActions +
		Math.max(saplingSpends, saplingOutputs);

	return ZIP317_MARGINAL_FEE * Math.max(ZIP317_GRACE_ACTIONS, logicalActions);
}

// ─── RPC / timeout constants ─────────────────────────────────────────────────

const RPC_BLOCK_HEIGHT_METHOD = 'getblockchaininfo';
const CONSENSUS_HEIGHT_OFFSET = 1;
const RPC_FETCH_TIMEOUT_MS = 20_000;

const resolveEnvNumber = (primaryKey: string, secondaryKey: string, fallback: number): number => {
	if (typeof process === 'undefined') return fallback;
	const raw = process.env?.[primaryKey] || process.env?.[secondaryKey] || '';
	const parsed = Number(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const PCZT_FETCH_TIMEOUT_MS = resolveEnvNumber('REACT_APP_ZCASH_PCZT_TIMEOUT_MS', 'ZCASH_PCZT_TIMEOUT_MS', 600_000);

const DEFAULT_SHIELDED_COMMISSION_BPS = resolveEnvNumber(
	'REACT_APP_ZCASH_SHIELDED_COMMISSION_BPS',
	'ZCASH_SHIELDED_COMMISSION_BPS',
	10,
);
const DEFAULT_SHIELDED_COMMISSION_UA =
	(typeof process !== 'undefined' && (process.env?.REACT_APP_ZCASH_SHIELDED_COMMISSION_UA || process.env?.ZCASH_SHIELDED_COMMISSION_UA)) ||
	'u1f35nskx9nx0mhccmytp4ycltj25f5s0cqulsnva9rufvmtpgja82cdggv9df99xll7qlf2u42w07q6qark4c9ss99kq40r4epslmwzcum5z3xdm30e2646d9uvepf0gn9stknyaf994lu30q9rqw7tplac8rxz7uldxtswe22wnud9szuztw444nxyvnf0cm9c8y66n5c6p8q0gf2l7';

const DUPLICATE_NULLIFIER_MARKERS = ['duplicate nullifier', 'double-spend'];

const normaliseApiKey = (value?: string | null): string | undefined => {
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
};

const resolveDefaultApiKey = (): string | undefined => {
	if (typeof process === 'undefined') return undefined;
	return normaliseApiKey(
		process.env?.VITE_ZCASH_API_KEY ||
		process.env?.REACT_APP_ZCASH_API_KEY ||
		process.env?.ZCASH_API_KEY,
	);
};

// ─── localStorage helpers ────────────────────────────────────────────────────

const _serializeCacheEntry = (entry: CachedNotes): any => ({
	...entry,
	notes: entry.notes.map((n: any) => {
		const clone = { ...n };
		for (const key of Object.keys(clone)) {
			if (typeof clone[key] === 'bigint') {
				clone[key] = `__bigint__${clone[key].toString()}`;
			}
		}
		return clone;
	}),
});

const _deserializeCacheEntry = (raw: any): CachedNotes => ({
	...raw,
	notes: (raw.notes || []).map((n: any) => {
		const clone = { ...n };
		for (const key of Object.keys(clone)) {
			if (typeof clone[key] === 'string' && clone[key].startsWith('__bigint__')) {
				clone[key] = BigInt(clone[key].slice('__bigint__'.length));
			}
		}
		return clone;
	}),
});

const _persistNoteCache = (): void => {
	try {
		const obj: Record<string, any> = {};
		for (const [key, value] of noteCache.entries()) {
			obj[key] = _serializeCacheEntry(value);
		}
		localStorage.setItem(NOTE_CACHE_STORAGE_KEY, JSON.stringify(obj));
	} catch { /* ignore */ }
};

const _hydrateNoteCache = (): void => {
	try {
		const raw = localStorage.getItem(NOTE_CACHE_STORAGE_KEY);
		if (!raw) return;
		const obj = JSON.parse(raw) as Record<string, any>;
		const now = Date.now();
		for (const [key, value] of Object.entries(obj)) {
			const hasNotes = Array.isArray(value?.notes) && value.notes.length > 0;
			const ttl = hasNotes ? NOTE_CACHE_TTL : EMPTY_NOTE_CACHE_TTL;
			if (typeof value?.timestamp === 'number' && now - value.timestamp < ttl) {
				noteCache.set(key, _deserializeCacheEntry(value));
			}
		}
		if (noteCache.size > 0) {
			console.log(`💾 Hydrated ${noteCache.size} note-cache entries from localStorage`);
		}
	} catch { /* ignore */ }
};

// ─── Birthday hints (skip the slow auto-detect on subsequent cold scans) ─────

// UFVK → known first-activity (birthday) height. Persisted across reloads so a
// returning wallet can start its scan directly from a height we've already
// proven is at or below its earliest note, avoiding the multi-minute, no-
// progress birthday auto-detect phase (and unlocking a real percent).
const birthdayHints = new Map<string, number>();

const _hydrateBirthdayHints = (): void => {
	try {
		const raw = localStorage.getItem(BIRTHDAY_HINT_STORAGE_KEY);
		if (!raw) return;
		const obj = JSON.parse(raw);
		if (obj && typeof obj === 'object') {
			for (const [k, v] of Object.entries(obj)) {
				const n = Number(v);
				if (Number.isFinite(n) && n > 0) birthdayHints.set(k, Math.floor(n));
			}
		}
	} catch { /* ignore */ }
};

const _persistBirthdayHints = (): void => {
	try {
		const obj: Record<string, number> = {};
		for (const [k, v] of birthdayHints.entries()) obj[k] = v;
		localStorage.setItem(BIRTHDAY_HINT_STORAGE_KEY, JSON.stringify(obj));
	} catch { /* ignore */ }
};

const getBirthdayHint = (ufvk: string): number | undefined => {
	if (!ufvk) return undefined;
	const v = birthdayHints.get(ufvk);
	return typeof v === 'number' && v > 0 ? v : undefined;
};

/**
 * Remember a known-good birthday for a UFVK. We only ever keep the LOWEST value
 * so a future scan can never start above a real note and miss it.
 */
const saveBirthdayHint = (ufvk: string, height: number): void => {
	if (!ufvk || !Number.isFinite(height) || height <= 0) return;
	const floored = Math.floor(height);
	const existing = birthdayHints.get(ufvk);
	if (existing !== undefined && existing <= floored) return;
	birthdayHints.set(ufvk, floored);
	_persistBirthdayHints();
};

// A freshly *generated* wallet cannot own any note that predates the moment it
// was created, so its birthday is effectively "now". Starting its very first
// scan a couple hundred blocks below the tip turns a multi-minute cold
// auto-detect into a few seconds, while staying safely below any note that
// might still land between generation and the first scan.
const FRESH_WALLET_BIRTHDAY_BUFFER_BLOCKS = 200;

// Hydrate on module load (browser only)
if (typeof window !== 'undefined') {
	_hydrateNoteCache();
	_hydrateBirthdayHints();
}

// ─── Utility helpers ─────────────────────────────────────────────────────────

type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

const getRawFetch = (): typeof fetch => {
	if (typeof window === 'undefined') return fetch;
	const w = window as any;
	return w.__WB32_FETCH_ORIGINAL__ || w.__WB32_NATIVE_FETCH__ || w.fetch;
};

const normaliseNullifierHex = (value: string | null | undefined): string | null => {
	if (typeof value !== 'string') return null;
	let hex = value.trim();
	if (!hex) return null;
	if (hex.startsWith('0x') || hex.startsWith('0X')) hex = hex.slice(2);
	hex = hex.replace(/[^0-9a-fA-F]/g, '');
	if (!hex) return null;
	return hex.toLowerCase();
};

const normaliseMnemonic = (phrase: string): string =>
	(phrase || '').trim().replace(/\s+/g, ' ');

const bytesToHex = (bytes: Uint8Array): string =>
	Array.prototype.map.call(bytes, (b: number) => b.toString(16).padStart(2, '0')).join('');

const hexToBytes = (hex: string): Uint8Array => {
	if (!hex) return new Uint8Array();
	const cleaned = hex.startsWith('0x') || hex.startsWith('0X') ? hex.slice(2) : hex;
	if (!/^[0-9a-fA-F]*$/.test(cleaned) || cleaned.length % 2 !== 0) {
		throw new Error('Invalid hex string');
	}
	const out = new Uint8Array(cleaned.length / 2);
	for (let i = 0; i < out.length; i++) {
		out[i] = parseInt(cleaned.slice(i * 2, i * 2 + 2), 16);
	}
	return out;
};

const normaliseScriptHex = (value?: string | null): string => {
	if (typeof value !== 'string') return '';
	let script = value.trim();
	if (!script) return '';
	if (script.startsWith('0x') || script.startsWith('0X')) script = script.slice(2);
	if (!/^[0-9a-fA-F]+$/.test(script) || script.length % 2 !== 0) return '';
	return script.toLowerCase();
};

const deriveTransparentKeyPair = async (mnemonic: string) => {
	const { validateMnemonic } = await import('@scure/bip39');
	const { wordlist } = await import('@scure/bip39/wordlists/english.js');
	const { mnemonicToSeedSync } = await import('@scure/bip39');
	const { HDKey } = await import('@scure/bip32');
	const { secp256k1 } = await import('@noble/curves/secp256k1.js');

	const normalised = normaliseMnemonic(mnemonic);
	if (!validateMnemonic(normalised, wordlist)) {
		throw new Error('Invalid mnemonic phrase provided');
	}
	const seed = mnemonicToSeedSync(normalised);
	const hdkey = (HDKey as any).fromMasterSeed(seed);
	const childKey = hdkey.derive(ZCASH_DERIVATION_PATH);
	if (!childKey.privateKey) {
		throw new Error('Failed to derive transparent private key');
	}
	const privateKeyBytes =
		childKey.privateKey instanceof Uint8Array ? childKey.privateKey : new Uint8Array(childKey.privateKey);
	const publicKeyBytes = secp256k1.getPublicKey(privateKeyBytes, true);
	return {
		privateKeyBytes,
		publicKeyBytes,
		privateKeyHex: bytesToHex(privateKeyBytes),
		publicKeyHex: bytesToHex(publicKeyBytes),
		secp256k1,
	};
};

const selectTransparentUtxos = (
	utxos: Array<{ satoshis: number }>,
	requiredZatoshis: number,
) => {
	const sorted = [...utxos].sort((a, b) => a.satoshis - b.satoshis);
	const selected: Array<{ satoshis: number }> = [];
	let total = 0;
	for (const utxo of sorted) {
		selected.push(utxo);
		total += utxo.satoshis;
		if (total >= requiredZatoshis) break;
	}
	if (total < requiredZatoshis) throw new Error('Insufficient transparent funds for shielding');
	return { selected, total };
};

// ─── Pending nullifier helpers ───────────────────────────────────────────────

const loadPendingNullifiers = (): void => {
	if (pendingNullifiersLoaded || typeof window === 'undefined') return;
	pendingNullifiersLoaded = true;
	try {
		const raw = window.localStorage.getItem(PENDING_NULLIFIER_STORAGE_KEY);
		if (!raw) return;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return;
		for (const [ufvk, entries] of Object.entries(parsed)) {
			if (!entries || typeof entries !== 'object') continue;
			const map = new Map<string, number>();
			for (const [nf, ts] of Object.entries(entries as Record<string, unknown>)) {
				const p = Number(ts);
				if (!Number.isFinite(p)) continue;
				const n = normaliseNullifierHex(nf);
				if (n) map.set(n, p);
			}
			if (map.size > 0) pendingNullifiersByUfvk.set(ufvk, map);
		}
	} catch (e) {
		console.warn('⚠️ Failed to load pending nullifiers:', e);
	}
};

const persistPendingNullifiers = (): void => {
	if (typeof window === 'undefined') return;
	try {
		const snapshot: Record<string, Record<string, number>> = {};
		for (const [ufvk, entries] of pendingNullifiersByUfvk.entries()) {
			if (!entries.size) continue;
			snapshot[ufvk] = {};
			for (const [nf, ts] of entries.entries()) snapshot[ufvk][nf] = ts;
		}
		if (Object.keys(snapshot).length === 0) {
			window.localStorage.removeItem(PENDING_NULLIFIER_STORAGE_KEY);
		} else {
			window.localStorage.setItem(PENDING_NULLIFIER_STORAGE_KEY, JSON.stringify(snapshot));
		}
	} catch (e) {
		console.warn('⚠️ Failed to persist pending nullifiers:', e);
	}
};

const getPendingNullifiersForUfvk = (ufvk: string): Set<string> => {
	loadPendingNullifiers();
	const entries = pendingNullifiersByUfvk.get(ufvk);
	if (!entries) return new Set();
	const now = Date.now();
	let changed = false;
	for (const [nf, ts] of entries.entries()) {
		if (!Number.isFinite(ts) || now - ts > PENDING_NULLIFIER_TTL_MS) {
			entries.delete(nf);
			changed = true;
		}
	}
	if (entries.size === 0) { pendingNullifiersByUfvk.delete(ufvk); changed = true; }
	if (changed) persistPendingNullifiers();
	return new Set(entries.keys());
};

const markPendingNullifiersForUfvk = (ufvk: string, nullifiers: string[]): void => {
	loadPendingNullifiers();
	if (!Array.isArray(nullifiers) || nullifiers.length === 0) return;
	const entries = pendingNullifiersByUfvk.get(ufvk) || new Map<string, number>();
	const now = Date.now();
	let changed = false;
	for (const nf of nullifiers) {
		const n = normaliseNullifierHex(nf);
		if (!n) continue;
		entries.set(n, now);
		changed = true;
	}
	if (changed) { pendingNullifiersByUfvk.set(ufvk, entries); persistPendingNullifiers(); }
};

const clearPendingNullifiersForUfvk = (ufvk: string, nullifiers?: string[]): void => {
	loadPendingNullifiers();
	const entries = pendingNullifiersByUfvk.get(ufvk);
	if (!entries) return;
	if (!Array.isArray(nullifiers) || nullifiers.length === 0) {
		pendingNullifiersByUfvk.delete(ufvk);
		persistPendingNullifiers();
		return;
	}
	let changed = false;
	for (const nf of nullifiers) {
		const n = normaliseNullifierHex(nf);
		if (n && entries.delete(n)) changed = true;
	}
	if (entries.size === 0) { pendingNullifiersByUfvk.delete(ufvk); changed = true; }
	if (changed) persistPendingNullifiers();
};

// ─── Fetch helpers ───────────────────────────────────────────────────────────

const fetchWithTimeout = async (
	input: FetchInput,
	init: FetchInit = {},
	timeoutMs?: number,
): Promise<Response> => {
	const rawFetch = getRawFetch();
	if (!timeoutMs || timeoutMs <= 0 || typeof AbortController === 'undefined') {
		return rawFetch(input, init);
	}
	const controller = new AbortController();
	const originalSignal = init?.signal;
	if (originalSignal) {
		if (originalSignal.aborted) controller.abort();
		else originalSignal.addEventListener('abort', () => controller.abort());
	}
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
	try {
		return await rawFetch(input, { ...(init || {}), signal: controller.signal });
	} finally {
		clearTimeout(timeoutId);
	}
};

const parseRateLimitRetryMs = (response: Response): number | null => {
	const retryAfter = response.headers.get('Retry-After');
	if (retryAfter) {
		const s = Number(retryAfter);
		if (Number.isFinite(s)) return Math.max(0, s * 1000);
		const d = Date.parse(retryAfter);
		if (!Number.isNaN(d)) return Math.max(0, d - Date.now());
	}
	const resetHeader = response.headers.get('RateLimit-Reset');
	if (!resetHeader) return null;
	const rv = Number(resetHeader);
	if (!Number.isFinite(rv)) return null;
	return rv > 1_000_000_000 ? Math.max(0, rv * 1000 - Date.now()) : Math.max(0, rv * 1000);
};

const safeParseJson = async (response: Response): Promise<any | null> => {
	try { return await response.json(); } catch { return null; }
};

const fetchLatestBlockHeight = async (rpcEndpoint: string): Promise<number | null> => {
	try {
		const response = await fetchWithTimeout(
			rpcEndpoint,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ jsonrpc: '1.0', id: 'latest-block', method: RPC_BLOCK_HEIGHT_METHOD, params: [] }),
			},
			RPC_FETCH_TIMEOUT_MS,
		);
		const payload = await response.json();
		const height = Number(payload?.result?.blocks ?? payload?.result?.height ?? payload?.result);
		if (Number.isFinite(height) && height > 0) return height;
	} catch (e) {
		console.warn('⚠️ Failed to fetch latest block height:', e);
	}
	return null;
};

// ═══════════════════════════════════════════════════════════════════════════════
// ShieldedTransactionClient
// ═══════════════════════════════════════════════════════════════════════════════

export class ShieldedTransactionClient {
	private webzjsClient: WebZjsKeysAndSendClient;
	private walletScanner: WalletScannerClientType;
	private network: string;
	private lightwalletdUrl: string;
	private apiBaseUrl: string;
	private walletScannerBaseUrl: string;
	private rpcEndpoint: string;
	private apiKey?: string;

	constructor(
		network: string = 'main',
		lightwalletdUrl: string = DEFAULT_LIGHTWALLETD_URL,
		walletScannerUrl?: string,
		apiBaseUrl?: string,
		rpcUrl?: string,
		apiKey?: string,
	) {
		this.network = network;
		this.lightwalletdUrl = lightwalletdUrl;
		this.apiBaseUrl = apiBaseUrl || walletScannerUrl || DEFAULT_WALLET_SCANNER_URL;
		this.walletScannerBaseUrl = walletScannerUrl || this.apiBaseUrl;
		this.rpcEndpoint = rpcUrl || DEFAULT_ZEBRA_RPC_URL;
		this.apiKey = normaliseApiKey(apiKey) || resolveDefaultApiKey();
		this.webzjsClient = getWebZjsClient(network, lightwalletdUrl);
		this.walletScanner = createWalletScannerClient({
			baseUrl: this.walletScannerBaseUrl,
			apiKey: this.apiKey,
		});
	}

	setApiKey(apiKey?: string): void {
		this.apiKey = normaliseApiKey(apiKey);
		this.walletScanner = createWalletScannerClient({
			baseUrl: this.walletScannerBaseUrl,
			apiKey: this.apiKey,
		});
	}

	private getApiHeaders(): Record<string, string> {
		const headers: Record<string, string> = { 'Content-Type': 'application/json' };
		if (this.apiKey) headers['X-API-Key'] = this.apiKey;
		return headers;
	}

	private getScannerAuth(): { apiKey?: string } | undefined {
		return this.apiKey ? { apiKey: this.apiKey } : undefined;
	}

	/** Initialize the client (installs fetch interceptor + WASM). */
	async initialize(): Promise<void> {
		try {
			installFetchInterceptor();
			await this.webzjsClient.initialize();
			console.log('✅ Shielded transaction client initialized');
		} catch (error: any) {
			console.warn('⚠️ WebZjs initialization failed (may not be needed for balance checks):', error?.message);
		}
	}

	// ── UFVK export ─────────────────────────────────────────────────────────

	private async exportUfvkLocal(mnemonic: string, accountIndex: number = 0): Promise<string> {
		const { validateMnemonic } = await import('@scure/bip39');
		const { wordlist } = await import('@scure/bip39/wordlists/english.js');
		const normalised = normaliseMnemonic(mnemonic);
		if (!validateMnemonic(normalised, wordlist)) throw new Error('Invalid mnemonic phrase provided');
		const ufvk = await this.webzjsClient.exportUnifiedFullViewingKey(normalised, { account: accountIndex });
		if (!ufvk) throw new Error('UFVK export failed');
		return ufvk;
	}

	/**
	 * Stamp a recent birthday hint for a freshly *generated* wallet so its first
	 * balance scan skips the slow, silent backwards auto-detect. Safe only for
	 * brand-new keys: an imported seed may have notes far below the current tip,
	 * so never call this for one.
	 *
	 * Best-effort — returns the stamped height, or null when the UFVK or chain
	 * tip can't be resolved (the normal first-scan detection then still runs).
	 */
	public async rememberFreshWalletBirthday(mnemonic: string, accountIndex: number = 0): Promise<number | null> {
		try {
			const ufvk = await this.exportUfvkLocal(mnemonic, accountIndex);
			if (!ufvk) return null;
			const tip = await fetchLatestBlockHeight(this.rpcEndpoint);
			if (typeof tip !== 'number' || tip <= 0) return null;
			const birthday = Math.max(1, tip - FRESH_WALLET_BIRTHDAY_BUFFER_BLOCKS);
			saveBirthdayHint(ufvk, birthday);
			console.log(`🎂 Fresh wallet birthday stamped at ${birthday} (tip ${tip})`);
			return birthday;
		} catch (e) {
			console.warn('Fresh-wallet birthday stamp skipped:', e);
			return null;
		}
	}

	// ── Balance ─────────────────────────────────────────────────────────────

	async getShieldedBalance(
		mnemonic: string,
		unifiedAddress: string,
		transparentAddress?: string,
		accountIndex: number = 0,
		birthdayHeight?: number,
		onProgress?: (balance: ShieldedBalance) => void,
		autoDetectBirthday: boolean = true,
		cancelSignal?: AbortSignal,
	): Promise<ShieldedBalance> {
		console.log('💰 Getting complete shielded balance breakdown...');
		console.log(`   Unified: ${unifiedAddress.substring(0, 20)}...`);
		if (transparentAddress) console.log(`   Transparent: ${transparentAddress}`);

		try {
			// 1. Transparent balance. Prefer the wallet-scanner UTXO set — it's the
			// exact source we spend from at shield time and resolves via
			// lightwalletd's GetAddressUtxos, whereas the `getaddressbalance` RPC
			// needs a Zebra/zcashd address index our node may not expose (it then
			// returns 0 even when the t-address holds funds, hiding the
			// shield-from-transparent option). Fall back to the RPC only if the
			// UTXO lookup yields nothing.
			if (cancelSignal?.aborted) throw new Error('Balance scan cancelled');
			let transparentBalance = 0n;
			if (transparentAddress) {
				try {
					const utxoResp = await this.walletScanner.getUtxos(transparentAddress);
					const utxos = Array.isArray(utxoResp?.data?.utxos) ? utxoResp.data.utxos : [];
					let sum = 0n;
					for (const u of utxos as Array<Record<string, any>>) {
						let satRaw: unknown = u?.satoshis ?? u?.value;
						if (satRaw === undefined || satRaw === null) {
							satRaw = Math.round(Number(u?.amount || 0) * ZATOSHIS_PER_ZEC);
						}
						const sat = Number(satRaw);
						if (Number.isFinite(sat) && sat > 0) sum += BigInt(Math.round(sat));
					}
					transparentBalance = sum;
					if (sum > 0n) console.log(`✅ Transparent (utxos): ${transparentBalance} zatoshis`);
				} catch (e) { console.warn('⚠️ Transparent UTXO lookup failed:', e); }

				if (transparentBalance === 0n) {
					try {
						const rpcResponse = await fetchWithTimeout(this.rpcEndpoint, {
							method: 'POST',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({
								jsonrpc: '1.0', id: 'transparent-balance',
								method: 'getaddressbalance',
								params: [{ addresses: [transparentAddress] }],
							}),
						}, RPC_FETCH_TIMEOUT_MS);
						const rpcData = await rpcResponse.json();
						if (rpcData.result && typeof rpcData.result.balance === 'number') {
							transparentBalance = BigInt(rpcData.result.balance);
							console.log(`✅ Transparent (rpc): ${transparentBalance} zatoshis`);
						}
					} catch (e) { console.warn('⚠️ Transparent balance RPC failed:', e); }
				}
			}

			// 2. Shielded balances via wallet scanner
			let saplingBalance = 0n;
			let orchardBalance = 0n;

			try {
				if (cancelSignal?.aborted) throw new Error('Balance scan cancelled');
				let ufvk = '';
				try {
					ufvk = await this.exportUfvkLocal(mnemonic, accountIndex);
				} catch (e) {
					throw new Error('UFVK export failed - local derivation unavailable');
				}

				// Check cache
				if (ufvk) {
					const cached = noteCache.get(ufvk);
					const nowTs = Date.now();
					const cachedNotes = Array.isArray(cached?.notes) ? cached.notes : [];
					const cachedHasNotes = cachedNotes.length > 0;
					const cacheTtlMs = cachedHasNotes ? NOTE_CACHE_TTL : EMPTY_NOTE_CACHE_TTL;
					const cachedHasWitness = cachedHasNotes ? cached?.hasWitnessData : true;
					const cacheLatestHeight = typeof cached?.latestHeight === 'number' ? cached.latestHeight : null;
					const latestHeight = await fetchLatestBlockHeight(this.rpcEndpoint);
					const cacheIsChainFresh = latestHeight === null
						? true
						: (cacheLatestHeight !== null && latestHeight <= cacheLatestHeight + CACHE_CHAIN_STALENESS_BLOCKS);

					if (
						cached &&
						nowTs - cached.timestamp < cacheTtlMs &&
						cachedHasWitness &&
						(!birthdayHeight || cached.birthdayHeight <= birthdayHeight) &&
						cacheIsChainFresh
					) {
						const cachedOrchardBalance = cachedNotes.reduce(
							(sum: bigint, note: any) => sum + BigInt(note?.value || 0), 0n,
						);
						const totalCached = transparentBalance + cachedOrchardBalance;
						console.log('💾 Using cached scan results (no scan required)');
						const cachedBalance: ShieldedBalance = {
							transparent: transparentBalance,
							sapling: 0n,
							orchard: cachedOrchardBalance,
							total: totalCached,
							formatted: {
								transparent: this.zatoshisToZecString(transparentBalance),
								sapling: '0.00000000',
								orchard: this.zatoshisToZecString(cachedOrchardBalance),
								total: this.zatoshisToZecString(totalCached),
							},
							scanProgress: {
								currentBlock: 0,
								latestBlock: cacheLatestHeight || latestHeight || 0,
								blocksScanned: 0,
								transactionsScanned: 0,
								notesFound: cachedNotes.length,
								percentComplete: 100,
								isScanning: false,
								birthdayHeight: cached.birthdayHeight,
								birthdayAutoDetected: false,
							},
						};
						if (onProgress) onProgress(cachedBalance);
						return cachedBalance;
					}
				}

				// Start scan job
				// Reuse a remembered birthday for this UFVK so we skip the slow,
				// silent auto-detect phase (and get a real progress percent).
				const effectiveBirthday = birthdayHeight || getBirthdayHint(ufvk);
				const useAutoDetect = autoDetectBirthday && !effectiveBirthday;
				const scanJobResponse = await this.walletScanner.startOrchardScanJob({
					ufvk,
					birthdayHeight: effectiveBirthday || undefined,
					autoDetect: useAutoDetect,
					witness: true,
				});
				if (!scanJobResponse.success || !scanJobResponse.data) {
					throw new Error(`Failed to start scan job: ${scanJobResponse.error || 'unknown error'}`);
				}

				const { jobId, jobToken } = scanJobResponse.data;
				console.log(`📋 Scan job started: ${jobId}`);

				if (onProgress) {
					onProgress({
						transparent: transparentBalance, sapling: 0n, orchard: 0n, total: transparentBalance,
						formatted: {
							transparent: this.zatoshisToZecString(transparentBalance),
							sapling: '0.00000000', orchard: '0.00000000',
							total: this.zatoshisToZecString(transparentBalance),
						},
						scanProgress: {
							currentBlock: 0, latestBlock: 0, blocksScanned: 0, transactionsScanned: 0,
							notesFound: 0, percentComplete: 0, isScanning: true, jobId,
						},
					});
				}

				// Poll for completion
				let attempts = 0;
				const maxAttempts = 240;
				let lastProgress = 0;
				let lastNotesFound = 0;

				while (attempts < maxAttempts) {
					if (cancelSignal?.aborted) {
						try { await this.walletScanner.cancelOrchardScanJob(jobId, { jobToken }); } catch { /* noop */ }
						throw new Error('Balance scan cancelled');
					}
					await new Promise(r => setTimeout(r, 5000));

					const jobStatus = await this.walletScanner.getOrchardScanJob(jobId, { jobToken });
					if (!jobStatus.success || !jobStatus.data) throw new Error('Failed to get job status');

					const job = jobStatus.data.job as Record<string, any>;
					const progress = job?.progress || {};
					const currentBlock = progress.currentStart || 0;
					const latestBlock = progress.latestHeight || 0;
					const blocksScanned = progress.blocksScanned || 0;
					const notesFound = progress.notesFound || 0;
					const transactionsScanned = progress.transactionsScanned || 0;
					const jobParams = job?.params || {};
					const birthdayHeightValue = typeof jobParams?.birthdayHeight === 'number'
						? jobParams.birthdayHeight
						: (typeof birthdayHeight === 'number' ? birthdayHeight : null);
					const birthdayAutoDetected = Boolean(jobParams?.birthdayHeightAutoDetected);
					const notes: any[] = Array.isArray(job?.results?.notes) ? job.results.notes : [];

					// Remember this UFVK's birthday so the next cold scan skips the
					// slow auto-detect. Only once a chain tip is known (detection
					// settled); saveBirthdayHint keeps the lowest known-good value.
					if (latestBlock > 0 && typeof birthdayHeightValue === 'number' && birthdayHeightValue > 0) {
						saveBirthdayHint(ufvk, birthdayHeightValue);
					}

					let partialOrchardBalance = 0n;
					if (notes.length > 0) {
						if (notes.every((n: any) => hasFullMerklePathWitness(n))) {
							const existing = noteCache.get(ufvk);
							if (!existing || !existing.hasWitnessData || existing.notes.length !== notes.length) {
								noteCache.set(ufvk, {
									notes: notes.map((n: any) => ({ ...n })),
									timestamp: Date.now(), ufvk,
									birthdayHeight: birthdayHeight || jobParams?.birthdayHeight || 0,
									hasWitnessData: true,
								});
								_persistNoteCache();
							}
						}
					}
					for (const note of notes) partialOrchardBalance += BigInt(note.value || 0);

					const shouldUpdate =
						currentBlock > lastProgress + 500 ||
						job.status !== 'running' ||
						notesFound !== lastNotesFound;

					if (shouldUpdate) {
						let percentDone: number | null = null;
						if (!birthdayAutoDetected && latestBlock > 0 && typeof birthdayHeightValue === 'number') {
							percentDone = Math.min(99.9, Math.round(
								(blocksScanned / Math.max(1, latestBlock - birthdayHeightValue)) * 100,
							));
						}
						lastProgress = currentBlock;
						lastNotesFound = notesFound;
						if (onProgress) {
							const partialTotal = transparentBalance + partialOrchardBalance;
							onProgress({
								transparent: transparentBalance, sapling: 0n, orchard: partialOrchardBalance,
								total: partialTotal,
								formatted: {
									transparent: this.zatoshisToZecString(transparentBalance),
									sapling: '0.00000000',
									orchard: this.zatoshisToZecString(partialOrchardBalance),
									total: this.zatoshisToZecString(partialTotal),
								},
								scanProgress: {
									currentBlock, latestBlock, blocksScanned, transactionsScanned, notesFound,
									percentComplete: percentDone,
									isScanning: job.status === 'running',
									jobId,
									birthdayHeight: birthdayHeightValue,
									birthdayAutoDetected,
								},
							});
						}
					}

					if (job.status === 'succeeded') {
						orchardBalance = partialOrchardBalance;
						if (ufvk) {
							const hasWitnessData = notes.length === 0 ? true : hasFullMerklePathWitness(notes[0]);
							noteCache.set(ufvk, {
								notes: notes.map((n: any) => ({ ...n })),
								timestamp: Date.now(), ufvk,
								birthdayHeight: birthdayHeight || jobParams?.birthdayHeight || 0,
								hasWitnessData,
								latestHeight: typeof latestBlock === 'number' ? latestBlock : undefined,
							});
							_persistNoteCache();
						}
						break;
					} else if (job.status === 'failed') {
						throw new Error(`Scan job failed: ${job?.error || 'unknown error'}`);
					} else if (job.status === 'cancelled') {
						throw new Error('Scan job was cancelled');
					}
					attempts++;
				}

				if (attempts >= maxAttempts) throw new Error('Scan job timed out after 20 minutes');
			} catch (error: any) {
				if (String(error?.message || '').toLowerCase().includes('cancelled')) throw error;
				console.warn('⚠️ Shielded balance scan failed:', error);
			}

			const total = transparentBalance + saplingBalance + orchardBalance;
			const balance: ShieldedBalance = {
				transparent: transparentBalance, sapling: saplingBalance, orchard: orchardBalance, total,
				formatted: {
					transparent: this.zatoshisToZecString(transparentBalance),
					sapling: this.zatoshisToZecString(saplingBalance),
					orchard: this.zatoshisToZecString(orchardBalance),
					total: this.zatoshisToZecString(total),
				},
				scanProgress: {
					currentBlock: 0, latestBlock: 0, blocksScanned: 0, transactionsScanned: 0,
					notesFound: 0, percentComplete: 100, isScanning: false,
				},
			};

			console.log(`💎 Balance: 🔓 ${balance.formatted.transparent} ZEC | 🌸 ${balance.formatted.orchard} ZEC | 💰 ${balance.formatted.total} ZEC`);
			return balance;
		} catch (error: any) {
			console.error('❌ Balance check failed:', error);
			throw new Error(`Failed to get shielded balance: ${error.message}`);
		}
	}

	// ── Send shielded transaction ───────────────────────────────────────────

	async sendShieldedTransaction(options: ShieldedTransactionOptions): Promise<string> {
		const {
			mnemonic, fromAddress, transparentAddress, toAddress, amountZec, memo,
			accountIndex = 0, birthdayHeight, autoDetectBirthday = true,
			allowTransparentInputs = false, skipShieldedScan = false,
			commissionBps = DEFAULT_SHIELDED_COMMISSION_BPS,
			commissionUnifiedAddress = DEFAULT_SHIELDED_COMMISSION_UA,
			onStatus, onProgress, abortSignal,
		} = options;

		const emitStatus = (msg: string) => {
			if (typeof onStatus !== 'function') return;
			try { onStatus(msg); } catch { /* ignore */ }
		};

		const emitProgress = (p: { percent?: number; currentBlock?: number; latestBlock?: number }) => {
			if (typeof onProgress !== 'function') return;
			try { onProgress(p); } catch { /* ignore */ }
		};

		// Thrown when the caller cancels mid-scan. A distinct message keeps it
		// out of the "Transaction rejected" error-mapping path.
		const SCAN_CANCELLED = 'Note scan cancelled';
		const throwIfAborted = () => {
			if (abortSignal?.aborted) throw new Error(SCAN_CANCELLED);
		};

		console.log(`🚀 Shielded tx: ${amountZec} ZEC → ${toAddress.substring(0, 20)}...`);
		emitStatus('Preparing shielded transaction...');

		let cacheKey = '';
		let ufvk = '';
		let pendingNullifiersForAttempt: string[] = [];
		let latestBlockHeight: number | null = null;
		let transparentFallbackEnabled = false;
		let skipShieldedScanRequested = false;

		try {
			await this.webzjsClient.initialize();

			// 1. Export UFVK
			emitStatus('Exporting UFVK...');
			ufvk = await this.exportUfvkLocal(mnemonic, accountIndex);
			cacheKey = ufvk;

			// Calculate amounts
			const amountZatoshis = Math.floor(amountZec * ZATOSHIS_PER_ZEC);
			const commissionRateBps = Number.isFinite(Number(commissionBps))
				? Math.max(0, Math.floor(Number(commissionBps))) : 0;
			const commissionRecipient = String(commissionUnifiedAddress || '').trim();
			const commissionEnabled =
				commissionRateBps > 0 && commissionRecipient.startsWith('u1') && commissionRecipient !== toAddress;
			const commissionZatoshis = commissionEnabled
				? Math.max(1, Math.floor((amountZatoshis * commissionRateBps) / 10_000)) : 0;

			let feeZatoshis = DEFAULT_SHIELDED_FEE_ZATOSHIS;
			let requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
			let requiredZatoshisBig = BigInt(requiredZatoshis);
			skipShieldedScanRequested = skipShieldedScan === true;
			transparentFallbackEnabled = allowTransparentInputs === true || skipShieldedScanRequested;

			if (transparentFallbackEnabled && !transparentAddress) {
				throw new Error('Transparent address is required to shield transparent funds.');
			}

			latestBlockHeight = await fetchLatestBlockHeight(this.rpcEndpoint);

			// 2. Scan / load notes
			let cached = noteCache.get(cacheKey);
			const now = Date.now();
			const cachedNotes = Array.isArray(cached?.notes) ? cached.notes : [];
			const cachedHasNotes = cachedNotes.length > 0;
			const cacheTtlMs = cachedHasNotes ? NOTE_CACHE_TTL : EMPTY_NOTE_CACHE_TTL;
			const cacheLatestHeight = typeof cached?.latestHeight === 'number' ? cached.latestHeight : null;
			const cacheIsChainFresh = latestBlockHeight === null
				? true
				: (cacheLatestHeight !== null && latestBlockHeight <= cacheLatestHeight + CACHE_CHAIN_STALENESS_BLOCKS);
			const cachedHasRho = cachedHasNotes
				? cachedNotes.some((n: any) => Boolean(n?.rho || n?.rho_hex)) : true;
			if (cached && cachedHasNotes && !cachedHasRho) {
				noteCache.delete(cacheKey);
				_persistNoteCache();
				clearNoteCacheForAddress(fromAddress);
				cached = undefined as any;
			}

			let notes: any[] = [];
			let useTransparentInputs = false;
			const pendingNullifiers = ufvk ? getPendingNullifiersForUfvk(ufvk) : new Set<string>();

			const cachedHasWitness = cachedHasNotes ? cached?.hasWitnessData : true;
			const canUseCache = cached &&
				(now - cached.timestamp < cacheTtlMs) &&
				cachedHasWitness &&
				(!birthdayHeight || cached.birthdayHeight <= birthdayHeight) &&
				cacheIsChainFresh && cachedHasRho;

			if (skipShieldedScanRequested) {
				notes = [];
			} else if (canUseCache) {
				notes = cachedNotes;
				emitStatus(cachedHasNotes ? `Using cached notes (${cachedNotes.length})...` : 'Using cached empty scan...');
			} else {
				// Run scan job. Reuse a remembered birthday for this UFVK to skip
				// the slow auto-detect phase (also unlocks a real progress percent).
				const effectiveBirthday = birthdayHeight || getBirthdayHint(ufvk);
				const useAutoDetect = autoDetectBirthday && !effectiveBirthday;
				const runScanJob = async (forceRescan: boolean) => {
					throwIfAborted();
					emitStatus(forceRescan ? 'Rescanning notes with witness data...' : 'Starting witness scan...');
					const scanJobResult = await this.walletScanner.startOrchardScanJob(
						{
							ufvk,
							birthdayHeight: effectiveBirthday || undefined,
							autoDetect: useAutoDetect,
							witness: true,
							forceRescan,
							endHeight: forceRescan ? (latestBlockHeight ?? undefined) : undefined,
						},
						this.getScannerAuth(),
					);
					if (!scanJobResult.success || !scanJobResult.data) throw new Error('Failed to start wallet scanner job');
					const { jobId, jobToken } = scanJobResult.data;

					let jobStatus: any;
					let pollCount = 0;
					const maxPolls = 240;
					const pollIntervalMs = 5000;
					const pollIntervalSeconds = Math.max(1, Math.floor(pollIntervalMs / 1000));

					while (pollCount < maxPolls) {
						throwIfAborted();
						await new Promise(r => setTimeout(r, pollIntervalMs));
						throwIfAborted();
						const statusResult = await this.walletScanner.getOrchardScanJob(jobId, { jobToken });
						if (!statusResult.success) throw new Error('Failed to get scan job status');
						jobStatus = statusResult.data?.job;
						const status = jobStatus?.status;
						if (status === 'succeeded') break;
						else if (status === 'failed') throw new Error(`Wallet scanner job failed: ${jobStatus?.error || 'Unknown error'}`);
						pollCount++;
						// Emit on every poll (~5s) so the UI shows live movement
						// instead of a status line that looks frozen for 20s+.
						const elapsed = pollCount * pollIntervalSeconds;
						const prog = jobStatus?.progress || {};
						// The job reports the scanned height as `currentStart` and the
						// chain tip as `latestHeight` (older builds: currentBlock /
						// latestBlock). Read both so "block X / Y" and a real percent
						// show during a mint, not just an elapsed-seconds counter.
						const current = Number(prog.currentStart ?? prog.currentBlock ?? 0);
						const latest = Number(prog.latestHeight ?? prog.latestBlock ?? 0);
						const birthday = Number(jobStatus?.params?.birthdayHeight ?? 0);
						let pctNum = Number(prog.percentComplete);
						if (!Number.isFinite(pctNum) && latest > 0 && birthday > 0 && current >= birthday) {
							pctNum = Math.min(99.9, ((current - birthday) / Math.max(1, latest - birthday)) * 100);
						}
						const hasPct = Number.isFinite(pctNum);
						if (latest === 0) {
							// No chain tip yet → the server is still auto-detecting the
							// wallet's first-activity height (the slow, silent phase).
							emitStatus(`Detecting wallet history… ${elapsed}s`);
							emitProgress({});
						} else {
							const pctStr = hasPct ? `${pctNum.toFixed(1)}%` : `${elapsed}s elapsed`;
							const blocks = current > 0
								? ` · block ${current.toLocaleString()}/${latest.toLocaleString()}`
								: '';
							emitStatus(`Scanning notes... ${pctStr}${blocks}`);
							emitProgress({
								percent: hasPct ? pctNum : undefined,
								currentBlock: current || undefined,
								latestBlock: latest || undefined,
							});
						}
					}
					if (jobStatus?.status !== 'succeeded') throw new Error('Wallet scanner job timed out');
					return jobStatus;
				};

				let jobStatus = await runScanJob(false);
				notes = jobStatus?.results?.notes || [];
				// Only force a second full rescan when we actually have notes
				// that are missing rho/witness data. An empty result means this
				// wallet has no spendable Orchard notes at all — a re-scan of the
				// same range can't conjure them, so skip it and fail fast with a
				// clear message instead of doubling the user's wait.
				if (notes.length > 0 && !notes.some((n: any) => Boolean(n?.rho || n?.rho_hex))) {
					jobStatus = await runScanJob(true);
					notes = jobStatus?.results?.notes || [];
				}
				emitStatus('Scan complete. Selecting notes...');

				const jobParams = (jobStatus as Record<string, any> | undefined)?.params || {};
				const actualBirthdayHeight = birthdayHeight || jobParams?.birthdayHeight || 0;
				// Remember the birthday so the next cold scan can skip auto-detect.
				saveBirthdayHint(ufvk, actualBirthdayHeight);
				const scanLatestHeight = (jobStatus as Record<string, any> | undefined)?.progress?.latestHeight;
				const hasWitnessData = notes.length === 0 ? true : hasFullMerklePathWitness(notes[0]);
				noteCache.set(cacheKey, {
					notes: notes.map((n: any) => ({ ...n })),
					timestamp: now, ufvk,
					birthdayHeight: actualBirthdayHeight,
					hasWitnessData,
					latestHeight: typeof scanLatestHeight === 'number' ? scanLatestHeight : (typeof latestBlockHeight === 'number' ? latestBlockHeight : undefined),
				});
				_persistNoteCache();
			}

			// Filter pending nullifiers
			if (notes.length > 0 && pendingNullifiers.size > 0) {
				notes = notes.filter((n: any) => {
					const nf = normaliseNullifierHex(this.getNoteNullifierHex(n));
					return !(nf && pendingNullifiers.has(nf));
				});
			}
			if (notes.length === 0 && pendingNullifiers.size > 0 && !transparentFallbackEnabled) {
				throw new Error('All spendable notes are pending confirmation from a previous shielded send.');
			}

			// Filter notes missing rho
			if (notes.length > 0) {
				notes = notes.filter((n: any) => Boolean(this.getNoteNullifierHex(n)) && Boolean(this.getNoteRhoHex(n)));
			}
			if (notes.length === 0 && !transparentFallbackEnabled) {
				throw new Error('No spendable notes with rho available. Rescan with updated scanner and retry.');
			}

			const totalAvailable = notes.reduce((sum: bigint, n: any) => sum + BigInt(n?.value || 0), 0n);
			useTransparentInputs = transparentFallbackEnabled && (notes.length === 0 || totalAvailable < requiredZatoshisBig);

			if (useTransparentInputs && notes.length > 0) {
				try {
					const partial = this.selectNotesForSpend(notes, totalAvailable);
					notes = partial.selectedNotes;
				} catch {
					const largest = notes.slice().sort((a: any, b: any) => Number(b?.value || 0) - Number(a?.value || 0))[0];
					notes = largest ? [largest] : [];
				}
			}

			if (!useTransparentInputs) {
				const selection = this.selectNotesForSpend(notes, requiredZatoshisBig);
				notes = selection.selectedNotes;
			}

			// 3. Derive USK + seed fingerprint
			emitStatus('Deriving unified spending key...');
			const { usk, seedFp } = await this.webzjsClient.generateUnifiedAddress(mnemonic, accountIndex);
			if (!seedFp || typeof (seedFp as any).to_bytes !== 'function') {
				throw new Error('Failed to derive seed fingerprint from WebZjs');
			}
			const seedFingerprintHex = bytesToHex((seedFp as any).to_bytes());

			// 4. Build PCZT on server
			emitStatus('Building PCZT on server...');

			let transparentInputsPayload: any[] = [];
			let transparentOutputsPayload: any[] = [];
			let changeUnifiedAddress: string | undefined;
			let allNormalisedUtxos: any[] = [];

			const isTransparentRecipient = toAddress.startsWith('t');
			const isUnifiedRecipient = toAddress.startsWith('u1');
			if (!isTransparentRecipient && !isUnifiedRecipient) {
				throw new Error('Recipient must be a unified (u1) or transparent (t) address');
			}
			if (memo && isTransparentRecipient) {
				throw new Error('Transparent recipients do not support memos');
			}

			const outputsPayload: any[] = [];
			if (isUnifiedRecipient) {
				outputsPayload.push({
					toUnifiedAddress: toAddress,
					valueZatoshis: amountZatoshis,
					memoHex: memo ? Buffer.from(memo).toString('hex') : undefined,
				});
			} else {
				transparentOutputsPayload.push({ address: toAddress, valueZatoshis: amountZatoshis, userAddress: toAddress });
			}
			if (commissionZatoshis > 0) {
				outputsPayload.push({ toUnifiedAddress: commissionRecipient, valueZatoshis: commissionZatoshis });
			}

			const spends = notes.map((note: any) => {
				const leafPositionRaw = note.leaf_position ?? note.leafPosition ?? note.leafPos ?? note.note_position ?? note.position;
				const siblings = extractMerklePathSiblings(note);
				const anchorRoot = this.getAnchorRootHex(note);
				const hasWitnessData = Boolean(anchorRoot) && leafPositionRaw !== undefined && siblings.length === ORCHARD_MERKLE_DEPTH;
				const noteNullifierHex = this.getNoteNullifierHex(note);
				if (!noteNullifierHex) throw new Error('Shielded note missing nullifier.');
				const rhoHex = this.getNoteRhoHex(note);
				if (!rhoHex) throw new Error('Shielded note missing rho.');

				const spend: Record<string, any> = {
					noteCommitmentHex: note.commitment,
					diversifierHex: note.note_plaintext?.diversifier || note.diversifier,
					pkdHex: note.note_plaintext?.pk_d || note.pk_d,
					rseedHex: note.note_plaintext?.rseed || note.rseed,
					rhoHex,
					valueZatoshis: note.value,
					anchorHeight: note.anchor_height,
					nullifierHex: noteNullifierHex,
					blockHeight: note.block_height,
				};

				if (hasWitnessData) {
					spend.anchorRootHex = anchorRoot;
					spend.leafPosition = String(leafPositionRaw);
					spend.merklePathSiblingsHex = siblings;
				} else {
					spend.orchardTreeHex = note.orchard_tree;
					spend.actionIndexInBlock = note.action_index_in_block;
				}
				return spend;
			});

			const shieldedInputTotalZatoshis = spends.reduce(
				(sum: number, s: any) => sum + Number(s.valueZatoshis || 0), 0,
			);
			let requiredTransparentZatoshis = useTransparentInputs
				? Math.max(0, requiredZatoshis - shieldedInputTotalZatoshis) : requiredZatoshis;

			if (!useTransparentInputs) {
				const totalInputZatoshis = spends.reduce((sum: bigint, s: any) => sum + BigInt(s.valueZatoshis || 0), 0n);
				if (totalInputZatoshis < requiredZatoshisBig) {
					throw new Error(`Insufficient note value. Inputs=${totalInputZatoshis} required=${requiredZatoshisBig}`);
				}
				const changeZatoshis = totalInputZatoshis - requiredZatoshisBig;
				if (changeZatoshis > 0n) {
					if (changeZatoshis > BigInt(Number.MAX_SAFE_INTEGER)) {
						throw new Error('Change amount exceeds JS safe integer range.');
					}
					changeUnifiedAddress = fromAddress;
				}
			}

			if (useTransparentInputs && requiredTransparentZatoshis > 0) {
				emitStatus('Selecting transparent UTXOs for shielding...');
				const utxoResponse = await this.walletScanner.getUtxos(transparentAddress || '');
				if (!utxoResponse?.success) throw new Error('Failed to fetch transparent UTXOs for shielding');

				const utxosRaw = Array.isArray(utxoResponse?.data?.utxos) ? utxoResponse.data.utxos : [];
				if (!utxosRaw.length) throw new Error('No transparent UTXOs available for shielding');

				const confirmedUtxos = utxosRaw.filter(
					(u: any) => typeof u?.confirmations !== 'number' || u.confirmations >= MIN_CONFIRMED_UTXO_CONFIRMATIONS,
				);
				const utxosToUse = confirmedUtxos.length ? confirmedUtxos : utxosRaw;

				const normalisedUtxos = utxosToUse.map((utxo: any) => {
					const txid = typeof utxo?.txid === 'string' ? utxo.txid.trim().toLowerCase() : '';
					if (!/^[0-9a-fA-F]{64}$/.test(txid)) throw new Error('Invalid transparent UTXO txid');
					const outputIndex = Number(utxo?.vout ?? utxo?.outputIndex ?? utxo?.output_index ?? utxo?.index);
					if (!Number.isInteger(outputIndex) || outputIndex < 0) throw new Error('Invalid transparent UTXO output index');
					let satRaw: unknown = utxo?.satoshis ?? utxo?.value;
					if (satRaw === undefined || satRaw === null) satRaw = Math.round(Number(utxo?.amount || 0) * ZATOSHIS_PER_ZEC);
					const satoshis = Number(satRaw);
					if (!Number.isFinite(satoshis) || satoshis <= 0) throw new Error('Invalid transparent UTXO amount');
					const scriptHex = normaliseScriptHex(utxo?.script || utxo?.scriptPubKey || utxo?.script_pub_key)
						|| bytesToHex(toZcashOutputScript(transparentAddress || ''));
					if (!scriptHex) throw new Error('Transparent UTXO scriptPubKey missing');
					return { txid, vout: outputIndex, satoshis, scriptPubKey: scriptHex };
				});

				allNormalisedUtxos = normalisedUtxos;
				const { selected, total } = selectTransparentUtxos(normalisedUtxos, requiredTransparentZatoshis);
				let change = total - requiredTransparentZatoshis;

				transparentInputsPayload = selected.map((utxo: any) => ({
					txid: utxo.txid, vout: utxo.vout, scriptPubKey: utxo.scriptPubKey,
					valueZatoshis: utxo.satoshis, sequence: 0xffffffff, sighashType: 1, address: transparentAddress,
				}));

				if (change > TRANSPARENT_DUST_THRESHOLD_ZATOSHIS) {
					transparentOutputsPayload.push({ address: transparentAddress, valueZatoshis: change, userAddress: transparentAddress });
				} else if (change > 0) {
					feeZatoshis += change;
				}
			}

			// ZIP-317 fee recalculation
			{
				const hasOrchard = spends.length > 0 || outputsPayload.length > 0;
				const orchardOutputCount = outputsPayload.length + (hasOrchard ? 1 : 0);
				const newFee = calculateZip317Fee({
					transparentInputs: transparentInputsPayload.length,
					transparentOutputs: transparentOutputsPayload.length,
					orchardSpends: spends.length,
					orchardOutputs: orchardOutputCount,
				});
				if (newFee !== feeZatoshis) {
					feeZatoshis = newFee;
					requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
					requiredZatoshisBig = BigInt(requiredZatoshis);

					if (useTransparentInputs && transparentInputsPayload.length > 0) {
						requiredTransparentZatoshis = Math.max(0, requiredZatoshis - shieldedInputTotalZatoshis);
						let totalTransparentIn = transparentInputsPayload.reduce((s: number, u: any) => s + (u.valueZatoshis || 0), 0);
						let newChange = totalTransparentIn - requiredTransparentZatoshis;

						if (newChange < 0 && allNormalisedUtxos.length > 0) {
							try {
								const { selected: reSel, total: reTotal } = selectTransparentUtxos(allNormalisedUtxos, requiredTransparentZatoshis);
								transparentInputsPayload = reSel.map((u: any) => ({
									txid: u.txid, vout: u.vout, scriptPubKey: u.scriptPubKey,
									valueZatoshis: u.satoshis, sequence: 0xffffffff, sighashType: 1, address: transparentAddress,
								}));
								totalTransparentIn = reTotal;
								newChange = totalTransparentIn - requiredTransparentZatoshis;
							} catch { /* fallthrough */ }
						}

						const recipientOutputs = transparentOutputsPayload.filter((o: any) => o.address !== transparentAddress);
						transparentOutputsPayload = [...recipientOutputs];
						if (newChange > TRANSPARENT_DUST_THRESHOLD_ZATOSHIS) {
							transparentOutputsPayload.push({ address: transparentAddress, valueZatoshis: newChange, userAddress: transparentAddress });
						} else if (newChange < 0) {
							throw new Error(`Insufficient transparent funds after ZIP-317 fee adjustment.`);
						} else if (newChange > 0) {
							feeZatoshis += newChange;
						}

						// Recalculate again if shape changed
						const finalFee = calculateZip317Fee({
							transparentInputs: transparentInputsPayload.length,
							transparentOutputs: transparentOutputsPayload.length,
							orchardSpends: spends.length,
							orchardOutputs: orchardOutputCount,
						});
						if (finalFee !== feeZatoshis) {
							feeZatoshis = finalFee;
							requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
							requiredZatoshisBig = BigInt(requiredZatoshis);
						}
					}
				}
				console.log(`💰 Final ZIP-317 fee: ${feeZatoshis} zatoshis`);
			}

			if (latestBlockHeight === null) latestBlockHeight = await fetchLatestBlockHeight(this.rpcEndpoint);
			const targetHeight = latestBlockHeight ? latestBlockHeight + CONSENSUS_HEIGHT_OFFSET : undefined;

			const buildPczt = () =>
				fetchWithTimeout(`${this.apiBaseUrl}/pczt/build`, {
					method: 'POST',
					headers: this.getApiHeaders(),
					body: JSON.stringify({
						network: 'main', seedFingerprintHex, accountIndex, ufvk,
						feeZatoshis, targetHeight, spends, outputs: outputsPayload,
						changeUnifiedAddress,
						transparent: (transparentInputsPayload.length || transparentOutputsPayload.length)
							? { inputs: transparentInputsPayload, outputs: transparentOutputsPayload } : undefined,
					}),
				}, PCZT_FETCH_TIMEOUT_MS);

			const maxBuildAttempts = 3;
			let pcztBuildResponse: Response | null = null;
			let priorRetryMs: number | null = null;
			for (let attempt = 0; attempt < maxBuildAttempts; attempt++) {
				pcztBuildResponse = await buildPczt();
				if (pcztBuildResponse.status !== 429) break;
				priorRetryMs = parseRateLimitRetryMs(pcztBuildResponse);
				const delayMs = priorRetryMs ?? 15_000 * Math.pow(2, attempt);
				if (attempt < maxBuildAttempts - 1) {
					await new Promise(r => setTimeout(r, delayMs));
				}
			}
			if (!pcztBuildResponse) throw new Error('PCZT building failed: no response from server');
			if (!pcztBuildResponse.ok) {
				const errorData = await safeParseJson(pcztBuildResponse);
				if (pcztBuildResponse.status === 429) {
					const retryMs = parseRateLimitRetryMs(pcztBuildResponse) ?? priorRetryMs;
					const hint = retryMs ? ` Retry after ${Math.ceil(retryMs / 1000)}s.` : '';
					throw new Error(`PCZT building failed: ${errorData?.error || 'Rate limit exceeded'}.${hint}`);
				}
				throw new Error(`PCZT building failed: ${errorData?.error || 'Unknown error'}`);
			}

			const pcztResponse = await pcztBuildResponse.json();
			let pcztBase64 = pcztResponse.pczt;
			let responseFormat = pcztResponse.pcztFormat || pcztResponse.pczt_format || 'base64';
			console.log('✅ PCZT built on server');

			// 5. Transparent signing (if needed)
			if (transparentInputsPayload.length > 0) {
				const transparentSighashes = pcztResponse.transparentSighashes || pcztResponse.transparent_sighashes || [];
				if (!Array.isArray(transparentSighashes) || transparentSighashes.length === 0) {
					throw new Error('Transparent inputs present but server did not return sighashes for signing');
				}
				emitStatus('Signing transparent inputs...');
				const keyPair = await deriveTransparentKeyPair(mnemonic);

				const signatures = await Promise.all(
					transparentSighashes.map(async (entry: any) => {
						const sighashHex = entry.sighashHex || entry.sighash_hex || entry.sighash;
						const sighashType = Number(entry.sighashType ?? entry.sighash_type ?? 1);
						if (!sighashHex) throw new Error('Transparent sighash missing from server response');
						const signResult = keyPair.secp256k1.sign(hexToBytes(String(sighashHex)), keyPair.privateKeyBytes, {
							lowS: true, prehash: false,
						});
						const signatureDer = ecdsaSignatureToDer(keyPair.secp256k1, signResult);
						const signatureBytes = new Uint8Array([...signatureDer, sighashType & 0xff]);
						return { index: entry.index, pubkeyHex: keyPair.publicKeyHex, signatureHex: bytesToHex(signatureBytes) };
					}),
				);

				const applyResponse = await fetchWithTimeout(`${this.apiBaseUrl}/pczt/apply-transparent-signatures`, {
					method: 'POST',
					headers: this.getApiHeaders(),
					body: JSON.stringify({ pczt: pcztBase64, signatures }),
				}, PCZT_FETCH_TIMEOUT_MS);
				if (!applyResponse.ok) {
					const errorData = await safeParseJson(applyResponse);
					throw new Error(`Transparent signature apply failed: ${errorData?.error || 'Unknown error'}`);
				}
				const signedResponse = await applyResponse.json();
				if (!signedResponse?.success || !signedResponse?.pczt) {
					throw new Error(signedResponse?.error || 'Transparent signature apply failed');
				}
				pcztBase64 = signedResponse.pczt;
				responseFormat = signedResponse.pcztFormat || signedResponse.pczt_format || responseFormat;
				emitStatus('Transparent inputs signed.');
			}

			// 6. Sign PCZT client-side (shielded)
			emitStatus('Signing PCZT in browser...');
			if (!pcztBase64) throw new Error('Server did not return PCZT data.');
			const format = responseFormat || 'base64';
			const unsignedPcztBytes = Buffer.from(pcztBase64, format === 'hex' ? 'hex' : 'base64');

			const PcztClass = getPcztClass();
			const pczt_sign_fn = getPcztSign();

			let unsignedPczt: IPczt;
			try {
				unsignedPczt = PcztClass.from_bytes(unsignedPcztBytes);
			} catch (parseError: any) {
				throw new Error(`PCZT parsing failed: ${parseError.message}`);
			}

			const network = this.network === 'main' ? 'main' : 'test';
			const signedPczt = await pczt_sign_fn(network, unsignedPczt, usk, seedFp);
			const signedPcztBase64 = Buffer.from(signedPczt.serialize()).toString('base64');
			console.log('✅ PCZT signed client-side');

			// 7. Broadcast
			emitStatus('Broadcasting transaction...');
			pendingNullifiersForAttempt = notes
				.map((n: any) => this.getNoteNullifierHex(n))
				.map(nf => normaliseNullifierHex(nf))
				.filter((nf): nf is string => Boolean(nf));
			if (pendingNullifiersForAttempt.length > 0 && ufvk) {
				markPendingNullifiersForUfvk(ufvk, pendingNullifiersForAttempt);
			}

			const broadcastResponse = await fetchWithTimeout(`${this.apiBaseUrl}/pczt/broadcast`, {
				method: 'POST',
				headers: this.getApiHeaders(),
				body: JSON.stringify({ signedPczt: signedPcztBase64, format: 'base64' }),
			}, PCZT_FETCH_TIMEOUT_MS);

			if (!broadcastResponse.ok) {
				const errorData = await broadcastResponse.json();
				throw new Error(`Broadcasting failed: ${errorData.error || 'Unknown error'}`);
			}

			const { txid } = await broadcastResponse.json();
			console.log(`✅ Transaction broadcast: ${txid}`);
			emitStatus(`Broadcast complete. TxID: ${txid}`);

			if (cacheKey) {
				noteCache.delete(cacheKey);
				_persistNoteCache();
				clearNoteCacheForAddress(fromAddress);
			}

			return txid;
		} catch (error: any) {
			console.error('❌ Shielded transaction failed:', error);
			const errorMessage = String(error?.message || '').toLowerCase();
			const isDuplicateNullifier = DUPLICATE_NULLIFIER_MARKERS.some(m => errorMessage.includes(m));
			if (pendingNullifiersForAttempt.length > 0 && ufvk && !isDuplicateNullifier) {
				clearPendingNullifiersForUfvk(ufvk, pendingNullifiersForAttempt);
			}

			if (error.message?.includes('AnchorMismatch')) {
				throw new Error(`AnchorMismatch. Retry or rescan with a narrower birthday height.\nDetails: ${error.message}`);
			} else if (isDuplicateNullifier) {
				throw new Error('Shielded note already spent or pending in mempool.');
			} else if (error.name === 'AbortError' || error.message?.includes('abort')) {
				throw new Error('Shielded transaction timed out. Please retry.');
			} else if (error.message?.includes('No spendable notes')) {
				throw new Error('No shielded funds available to spend.');
			} else if (error.message?.includes('Insufficient')) {
				throw new Error(`Insufficient shielded funds. Requested: ${options.amountZec} ZEC`);
			} else {
				throw new Error(`Shielded transaction failed: ${error.message}`);
			}
		}
	}

	// ── Note helpers (private) ──────────────────────────────────────────────

	private getAnchorRootHex(note: any): string | null {
		const v = note?.anchor_root ?? note?.anchorRootHex ?? note?.anchor;
		return typeof v === 'string' && v.trim() ? v.trim() : null;
	}

	private getNoteNullifierHex(note: any): string | null {
		return normaliseNullifierHex(
			note?.note_nullifier ?? note?.noteNullifier ?? note?.nullifier ??
			note?.nullifier_hex ?? note?.nullifierHex ??
			note?.note?.nullifier ?? note?.note?.note_nullifier ??
			note?.note?.nullifierHex ?? note?.note?.nullifier_hex,
		);
	}

	private getNoteRhoHex(note: any): string | null {
		return normaliseNullifierHex(
			note?.rho ?? note?.rho_hex ?? note?.note?.rho ??
			note?.note?.rho_hex ?? note?.note_plaintext?.rho ?? note?.note_plaintext?.rho_hex,
		);
	}

	private selectNotesForSpend(
		notes: any[],
		requiredZatoshis: bigint,
	): { selectedNotes: any[]; anchorRoot?: string } {
		if (!notes.length) throw new Error('No spendable notes available.');
		if (requiredZatoshis <= 0n) throw new Error('Required amount must be > 0.');

		type Candidate = { anchorRoot: string; selectedNotes: any[]; total: bigint; change: bigint };
		const anchorGroups = new Map<string, any[]>();
		const singleNoteCandidates: { note: any; value: bigint; anchorRoot?: string }[] = [];
		const seenNullifiers = new Set<string>();

		for (const note of notes) {
			if (note?.spent || note?.is_spent || note?.isSpent) continue;
			const nf = this.getNoteNullifierHex(note);
			if (!nf || seenNullifiers.has(nf)) continue;
			seenNullifiers.add(nf);
			const value = BigInt(note?.value || 0);
			if (value <= 0n) continue;
			const anchorRoot = this.getAnchorRootHex(note);
			const hasWitness = hasFullMerklePathWitness(note);
			const hasTree = Boolean(note?.orchard_tree);
			if (!hasWitness && !hasTree) continue;
			if (anchorRoot && hasWitness) {
				const group = anchorGroups.get(anchorRoot) || [];
				group.push(note);
				anchorGroups.set(anchorRoot, group);
			}
			if (value >= requiredZatoshis) singleNoteCandidates.push({ note, value, anchorRoot: anchorRoot || undefined });
		}

		const candidates: Candidate[] = [];
		for (const [anchorRoot, group] of anchorGroups) {
			const sorted = group.slice().sort((a, b) => Number(b?.value || 0) - Number(a?.value || 0));
			let total = 0n;
			const selected: any[] = [];
			for (const n of sorted) {
				total += BigInt(n?.value || 0);
				selected.push(n);
				if (total >= requiredZatoshis) break;
			}
			if (total >= requiredZatoshis) {
				candidates.push({ anchorRoot, selectedNotes: selected, total, change: total - requiredZatoshis });
			}
		}

		if (candidates.length) {
			candidates.sort((a, b) => {
				const diff = a.selectedNotes.length - b.selectedNotes.length;
				if (diff !== 0) return diff;
				return a.change < b.change ? -1 : a.change > b.change ? 1 : 0;
			});
			return { selectedNotes: candidates[0].selectedNotes, anchorRoot: candidates[0].anchorRoot };
		}
		if (singleNoteCandidates.length) {
			singleNoteCandidates.sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
			return { selectedNotes: [singleNoteCandidates[0].note], anchorRoot: singleNoteCandidates[0].anchorRoot };
		}
		throw new Error('No set of spendable notes share the same anchor root with sufficient funds.');
	}

	private zatoshisToZecString(zatoshis: bigint): string {
		return (Number(zatoshis) / ZATOSHIS_PER_ZEC).toFixed(8);
	}

	// ── Spendable notes ─────────────────────────────────────────────────────

	async getSpendableNotes(mnemonic: string, accountIndex = 0, birthdayHeight?: number): Promise<ShieldedNote[]> {
		try {
			const ufvk = await this.exportUfvkLocal(mnemonic, accountIndex);
			const scanResult = await this.walletScanner.scanUfvkImmediate({ ufvk, birthdayHeight: birthdayHeight || 0 });
			if (!scanResult.success || !scanResult.data?.notes) return [];
			return scanResult.data.notes.map((note: any) => ({
				txHash: note.tx_hash || '',
				blockHeight: note.block_height || 0,
				value: note.value || 0,
				pool: 'orchard' as const,
				nullifier: this.getNoteNullifierHex(note) || undefined,
				commitment: note.commitment,
			}));
		} catch (e) {
			console.error('❌ Failed to get spendable notes:', e);
			return [];
		}
	}

	/** Validate if an address can be used for shielded transactions. */
	async validateShieldedAddress(address: string): Promise<{ isValid: boolean; type: string; canReceiveShielded: boolean }> {
		const validation = await this.webzjsClient.validateAddress(address);
		return {
			...validation,
			canReceiveShielded: validation.type === 'unified' || validation.type === 'sapling' || validation.type === 'orchard',
		};
	}
}

// ─── Singleton ───────────────────────────────────────────────────────────────

let globalShieldedClient: ShieldedTransactionClient | null = null;

export function getShieldedTransactionClient(
	network: string = 'main',
	lightwalletdUrl?: string,
	walletScannerUrl?: string,
	apiBaseUrl?: string,
	rpcUrl?: string,
	apiKey?: string,
): ShieldedTransactionClient {
	if (!globalShieldedClient) {
		globalShieldedClient = new ShieldedTransactionClient(
			network,
			lightwalletdUrl,
			walletScannerUrl,
			apiBaseUrl,
			rpcUrl,
			apiKey,
		);
	} else if (apiKey !== undefined) {
		globalShieldedClient.setApiKey(apiKey);
	}
	return globalShieldedClient;
}

